import * as React from "react";

// jaza-soft
import { PageHeader } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";
import CardHeader from "../../../../components/CardHeader";

import useQuery from "../../../../hooks/useQuery";
import { isEmpty, toFixed, toFixedValue } from "../../../../utils/helpers";
import { format, formatDateTime } from "../../../../utils/dateUtils";
import useSafeState from "../../../../hooks/useSafeState";
import { useDeepMemo, useEntityPrivilege, useSetting, useTime } from "../../../../hooks";
import { Entity, QcStatus } from "../../../../utils/types";
import { colorLightGreen, colorOrange, colorSecondary, colorTomatoRed } from "../../../../utils/colors";
import SettingKeys from "../../../../utils/SettingKeys";
import hasPrivilege from "../../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../../utils/privilege";
import useWarehouse from "../../../../hooks/useWarehouse";
import { UOM } from "../../../../constants";

import WarehouseTransferDrawer from "../WarehouseTransferDrawer";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
          ? colorTomatoRed.dark
          : value === QcStatus.ON_HOLD
          ? colorOrange.dark
          : value === QcStatus.PENDING
          ? colorSecondary.dark
          : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const MaterialTransferInvoiceView = props => {
  const { roles, hasAccess, history } = props;
  const fabricItemId = props.match.params.id;

  const [version, setVersion] = useSafeState(0);

  const [filterValues, setFilterValues] = useSafeState({});
  const [drawerState, setDrawerState] = useSafeState({ open: false, rolls: [], ledger: null });

  const { disableCreate } = useEntityPrivilege("MaterialTransfer");
  const { utc_offset } = useTime();
  const { allWhList } = useWarehouse();

  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const fabricFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRICS);
  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);
  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);

  const { data: fabricItem = {} } = useQuery(fabricItemId && { url: `fabric-items/${fabricItemId}`, version });
  const { data: rollList = [] } = useQuery(
    !isEmpty(fabricItemId) && {
      url: "rolls",
      params: { search: `fabricItem.id==${fabricItemId};parentId=='NULL'`, size: 10000 },
      version
    }
  );
  const { data: whLedgerList = [] } = useQuery(
    !isEmpty(fabricItemId) && {
      url: "ledgers",
      params: { search: `fabricItem.id==${fabricItemId};type=='WH_TRANSFER'`, size: 10000 },
      version
    }
  );

  const infoColumns = useDeepMemo(() => {
    // let fields = [...invoiceFields, ...fabricFields, ...fabricItemFields, "effFabColor", "fabricPo", ""];
    // if (allWhList?.length > 1) {
    //   fields.push("warehouse");
    // }

    let columns = [
      { dataKey: "invoiceNo", title: "Invoice No", linkPath: row => history.push(`/warehouse/invoices/${row.invoiceId}/view`) },
      { dataKey: "erpInvoiceNo", title: "ERP Invoice No", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "supplier", title: "Supplier" },
      { dataKey: "itemCode", title: "Item Code" },
      { dataKey: "itemDesc", title: "Item Description", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "articleName", title: "article Name", render: value => (isEmpty(value) ? "-" : value) },
      { dataKey: "effFabColor", title: "Fabric Color" },
      { dataKey: "fabricPo", title: "Fabric PO", render: value => (isEmpty(value) ? "-" : value) },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`)
      },
      { dataKey: "invoiceQty", title: "Invoice Qty", render: (value, _, row) => `${toFixedValue(value)} ${UomCodeFromValue[row.uomLength]}` },
      { dataKey: "grn", title: "GRN" },
      { dataKey: "grnDate", title: "GRN Date" },
      { dataKey: "grnQty", title: "GRN Qty", render: (value, _, row) => `${toFixedValue(value)} ${UomCodeFromValue[row.uomLength]}` },
      { dataKey: "warehouse", title: "GRN Warehouse" }
    ];

    // return columns.filter(c => fields.includes(c.dataKey));
    return columns;
  }, [invoiceFields, fabricFields, fabricItemFields, allWhList]);

  const columnList = useDeepMemo(() => {
    let columns = [
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "rollNo", title: "Roll No" },
      { dataKey: "invStatus", title: "Inv. Status", align: "center", filterOptions: { filter: true } },
      { dataKey: "qcStatus", title: "QC Status", align: "center", filterOptions: { filter: true }, render: renderQcStatus },
      { dataKey: "length", title: "Length", render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 2)} m`) },
      { dataKey: "weight", title: "Weight", render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 3)} kg`) },
      {
        dataKey: "currWarehouse",
        align: "right",
        filterOptions: { filter: true },
        title: "Current \nWarehouse",
        render: (value, shortValue) => shortValue || "-"
      }
    ];
    if (rollList.every(e => isEmpty(e.weight) || e.weight === 0.0)) {
      columns = columns.filter(c => c.dataKey !== "weight");
    }
    return columns;
  }, [rollFields, rollList]);

  const infoRows = useDeepMemo(() => {
    if (isEmpty(fabricItem)) return [];
    const infoRows = [
      {
        ...fabricItem.invoice,
        ...fabricItem.fabric,
        ...fabricItem,
        invoiceId: fabricItem.invoice?.id,
        grnDate: !isEmpty(utc_offset) && !isEmpty(fabricItem?.grnDate) ? format(fabricItem.grnDate, utc_offset, "dd MMM, yy") : "-",
        supplier: fabricItem.invoice?.supplier?.name,
        warehouse: allWhList?.find(e => e.code === fabricItem.warehouse)?.name
      }
    ];
    return infoRows;
  }, [fabricItem, utc_offset, allWhList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(rollList)) return [];

    const rows = rollList
      .sort((a, b) => (a.rollNo < b.rollNo ? -1 : 1))
      .map(({ parentId, ...roll }) => ({
        ...roll,
        fabricItem,
        millLength: roll.supplierRoll?.supplierLength,
        availableLength: roll.length,
        grnWarehouse: allWhList?.find(w => w.code === fabricItem.warehouse)?.name || fabricItem.warehouse,
        currWarehouse: allWhList?.find(w => w.code === roll.currWarehouse)?.name || roll.currWarehouse,
        grnWarehouseCode: fabricItem.warehouse,
        currWarehouseCode: roll.currWarehouse
      }));
    return rows;
  }, [rollList, fabricItem]);

  const whTxnRows = useDeepMemo(() => {
    if (isEmpty(whLedgerList)) return [];
    return whLedgerList
      .sort((a, b) => b.id - a.id)
      .map(ledger => {
        return {
          ...ledger,
          externalId: !isEmpty(ledger.externalIds) ? JSON.parse(ledger.externalIds)[0]?.externalId : "",
          fromWarehouse: allWhList?.find(w => w.code === ledger.fromWarehouse)?.name || ledger.fromWarehouse,
          toWarehouse: allWhList?.find(w => w.code === ledger.toWarehouse)?.name || ledger.toWarehouse,
          invoiceId: ledger.invoice?.id,
          invoiceNo: ledger.invoice?.invoiceNo,
          grn: ledger.fabricItem?.grn,
          fabricId: ledger.fabric?.id,
          itemCode: ledger.fabric?.itemCode,
          itemDesc: ledger.fabric?.itemCode,
          effFabColor: ledger.fabricItem?.effFabColor,
          colorShade: ledger.fabricItem?.colorShade,
          fabricPo: ledger.fabricItem?.fabricPo,
          orderingWidth: ledger.fabricItem?.orderingWidth,
          uomWidth: ledger.fabricItem?.uomWidth,
          uomLength: ledger.fabricItem?.uomLength,
          transferredQty: ledger.qty,
          time: !isEmpty(utc_offset) ? formatDateTime(ledger.createdAt, utc_offset) : "-",
          user: ledger.createdBy
        };
      });
  }, [whLedgerList, allWhList, utc_offset]);

  const whTxnColumns = useDeepMemo(() => {
    // let fields = [...invoiceFields, ...fabricFields, ...fabricItemFields, "associatedQty", "allocatedQty"];
    // if (allWhList?.length > 1) {
    //   fields.push("warehouse");
    // }
    let columns = [
      { dataKey: "txn", title: "TXN", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "externalId", title: "Ext. TXN", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "challanNo", title: "Challan No", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "ackNo", title: "Ack No", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "fromWarehouse", title: "From Warehouse" },
      { dataKey: "toWarehouse", title: "To Warehouse" },
      {
        dataKey: "invoiceNo",
        title: "Invoice No",
        render: (value, shortValue) => shortValue || "-",
        linkPath: row => history.push(`/warehouse/invoices/${row.invoiceId}/view`),
        filterOptions: { filter: true }
      },
      { dataKey: "grn", title: "GRN", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true } },
      {
        dataKey: "itemCode",
        title: "Item Code",
        render: (value, shortValue) => shortValue || "-",
        filterOptions: { filter: true },
        linkPath: row => history.push(`/warehouse/fabrics/${row.fabricId}/view`)
      },
      { dataKey: "itemDesc", title: "Item Description", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "effFabColor", title: "Fabric Color" },
      { dataKey: "colorShade", title: "Color Shade", filterOptions: { filter: true } },
      { dataKey: "fabricPo", title: "Fabric PO" },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`),
        filterOptions: { filter: true }
      },
      {
        dataKey: "transferredQty",
        title: "Transferred Qty",
        render: (value, _, row) =>
          isEmpty(value)
            ? ""
            : `${toFixed(value)} m${row.uomLength !== UOM.METER.value ? ` (${toFixedValue(row.ledgerQty)} ${UomCodeFromValue[row.uomLength]})` : ""}`
      },
      { dataKey: "time", title: "Time" },
      { dataKey: "user", title: "User", filterOptions: { filter: true } },
      { dataKey: "remarks", title: "Remarks" },
    ];

    if (whTxnRows.every(row => isEmpty(row.externalId))) {
      columns = columns.filter(c => c.dataKey !== "externalId");
    }
    if (whTxnRows.every(row => isEmpty(row.ackNo))) {
      columns = columns.filter(c => c.dataKey !== "ackNo");
    }

    return columns;
  }, [invoiceFields, fabricFields, fabricItemFields, allWhList, whTxnRows]);

  const selectActions = useDeepMemo(() => {
    let actions = [
      {
        name: "transfer",
        tooltip: "Transfer",
        showLabel: true,
        options: { variant: "contained", color: "primary", style: { marginLeft: "1em", width: 120 } }
      }
    ];
    return actions;
  }, []);

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (action === "transfer") {
      setDrawerState({ open: true, rollList: selectedRows });
    }
  };

  const fileteredRows = rows
    .filter(row => isEmpty(filterValues?.consignment) || filterValues?.consignment === row.consignment)
    .filter(row => isEmpty(filterValues?.shade) || filterValues?.shade === row.shade)
    .filter(row => isEmpty(filterValues?.csv) || filterValues?.csv === row.csv)
    .filter(row => isEmpty(filterValues?.qcStatus) || filterValues?.qcStatus === row.qcStatus);
  const availableLength = fileteredRows.reduce((acc, e) => acc + e.length, 0);
  const availableWeight = fileteredRows.reduce((acc, e) => acc + (e.weight || 0), 0);

  const title = `Roll Inventory: ${Number(toFixed(availableLength, 1))} m${availableWeight > 0 ? ` (${toFixedValue(availableWeight)} kg)` : ""}`;

  const titleSelected = selectedRows => {
    const selectedLength = selectedRows.reduce((acc, e) => acc + e.length, 0);
    const selectedWeight = selectedRows.reduce((acc, e) => acc + (e.weight || 0), 0);

    return `${selectedRows?.length} Rolls: ${toFixed(selectedLength)} m ${selectedWeight > 0 ? ` (${toFixed(selectedWeight)} kg)` : ""}  selected`;
  };

  return (
    <div>
      {/** Header Element */}
      <PageHeader title="Material Transfer - By Invoice" />

      <WarehouseTransferDrawer
        open={drawerState?.open || false}
        ledger={drawerState?.ledger}
        rollList={drawerState?.rollList}
        roles={roles}
        hasAccess={hasAccess}
        onRefresh={() => setVersion(v => v + 1)}
        onDrawerClose={() => setDrawerState({ open: false })}
      />

      <div style={{ margin: "1.5em", marginTop: 0 }}>
        {/* PO Details */}
        <MuiTable title="Item Details" toolbar columns={infoColumns} rows={infoRows} i18nKey="resources.fabricItems.fields" />

        {/* Warehouse Transfer Transactions */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={<CardHeader style={{ padding: 0 }} title="Transfer History" />}
            columns={whTxnColumns}
            rows={whTxnRows}
            onRowClick={row =>
              setDrawerState({
                open: true,
                rollList: Array.isArray(rows) ? rows.filter(ri => row.rollList?.map(e => e.id).includes(ri.id)) : row.rollList,
                ledger: row
              })
            }
            searchable
            pageable
            searchKeys={["txn", "challanNo", "ackNo", "fabricPo"]}
            i18nKey="resources.quality.fields.invoice"
          />
        </div>

        {/** Roll Inventory */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            key={version}
            title={title}
            titleSelected={titleSelected}
            columns={columnList}
            rows={rows}
            pageable
            searchable
            selectable={hasPrivilege(roles, hasAccess, ResourceId.MATERIAL_TRANSFER, Scope.WRITE) && !disableCreate}
            searchKeys={["rollNo"]}
            selectActions={selectActions}
            onSelectActionClick={onSelectActionClick}
            onFilter={setFilterValues}
            fontSize={16}
            pageSize={15}
            rowsPerPageOptions={[15, 25, 100]}
            i18nKey="resources.rolls.fields"
          />
        </div>
      </div>
    </div>
  );
};

export default MaterialTransferInvoiceView;
